import { Component, Input, OnChanges, ChangeDetectionStrategy } from '@angular/core';
import { ApiService } from '../../../services/api.service';
import { DeviceExclusionReason, IDeviceStatus, EXCLUSION_REASON_OPTIONS, getExclusionReasonKey } from '../../../models/devicestatus.model';
import { LocationModel } from '../../../models/location.model';
import { Device } from '../../../models/device.model';
import { TranslateModule } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { RemoveExclusionDialogComponent } from '../../dashboard/devicestatus/remove-exclusion-dialog.component';
import { DeviceService } from '../../../services/device.service';

@Component({
  selector: 'location-equipment-status',
  templateUrl: './location-equipment-status.component.html',
  styleUrls: ['./location-equipment-status.component.css'],
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [FormsModule, MatSelectModule, MatFormFieldModule, MatDialogModule, TranslateModule]
})
export class LocationEquipmentStatusComponent implements OnChanges {

  @Input() location: LocationModel;
  @Input() devices: Device[] = [];

  availableDevices: { id: string; name: string }[] = [];
  excludedDevices: IDeviceStatus[] = [];
  private locationDevices: { id: string; name: string }[] = [];
  selectedDeviceIds: string[] = [];
  selectedReason: DeviceExclusionReason | null = null;
  allSelected: boolean = false;
  excludeError: boolean = false;
  excludeSuccess: boolean = false;

  readonly reasonOptions = EXCLUSION_REASON_OPTIONS;

  constructor(private api: ApiService, private dialog: MatDialog, private deviceService: DeviceService) {}

  ngOnChanges(): void {
    if (this.location) {
      this.reset();
      const minDate = new Date('0001-01-01T00:00:00Z').getTime();
      this.locationDevices = (this.devices || [])
        .filter(d => d.locationId === this.location.id
          && !this.deviceService.isDeviceTypeWithoutStatus(d.deviceType)
          && d.lastConnection != null
          && new Date(d.lastConnection as any).getTime() !== minDate)
        .map(d => ({ id: d.id, name: d.name || d.id }));
      this.loadExcluded();
    }
  }

  private reset(): void {
    this.selectedDeviceIds = [];
    this.allSelected = false;
    this.selectedReason = null;
    this.excludeError = false;
    this.excludeSuccess = false;
  }

  loadExcluded(): void {
    this.api.getExcludedDevicesByLocation(this.location.id).subscribe({
      next: (excluded: IDeviceStatus[]) => {
        this.excludedDevices = excluded || [];
        this.rebuildAvailable();
      },
      // Without the exclusions the devices are still listed
      error: () => {
        this.excludedDevices = [];
        this.rebuildAvailable();
      }
    });
  }

  private rebuildAvailable(): void {
    this.availableDevices = [...this.locationDevices];
    // Drop any selected ids that are no longer available
    const availableIds = new Set(this.availableDevices.map(d => d.id));
    this.selectedDeviceIds = this.selectedDeviceIds.filter(
      id => id === '__select_all__' || availableIds.has(id)
    );
    this.allSelected = this.availableDevices.length > 0 &&
      this.selectedDeviceIds.filter(id => id !== '__select_all__').length === this.availableDevices.length;
  }

  isExcluded(id: string): boolean {
    return this.excludedDevices.some(d => d.id === id);
  }

  get realSelectedDevices(): { id: string; name: string }[] {
    return this.availableDevices.filter(d => this.selectedDeviceIds.includes(d.id));
  }

  onSelectionChange(): void {
    const hasSentinel = this.selectedDeviceIds.includes('__select_all__');
    const withoutSentinel = this.selectedDeviceIds.filter(id => id !== '__select_all__');

    if (hasSentinel) {
      if (this.allSelected) {
        if (withoutSentinel.length < this.availableDevices.length) {
          this.selectedDeviceIds = withoutSentinel;
          this.allSelected = false;
        }
      } else {
        this.selectedDeviceIds = ['__select_all__', ...this.availableDevices.map(d => d.id)];
        this.allSelected = true;
      }
    } else {
      if (this.allSelected) {
        this.selectedDeviceIds = [];
        this.allSelected = false;
      } else {
        if (this.availableDevices.length > 0 && withoutSentinel.length === this.availableDevices.length) {
          this.selectedDeviceIds = ['__select_all__', ...withoutSentinel];
          this.allSelected = true;
        }
      }
    }
  }

  excludeDevices(): void {
    if (!this.selectedDeviceIds.length || this.selectedReason === null) return;
    this.excludeError = false;
    this.excludeSuccess = false;

    const items = this.selectedDeviceIds
      .filter(id => id !== '__select_all__')
      .map(id => ({ deviceId: id, exclusionReason: this.selectedReason }));
    this.api.setDeviceExclusions(items).subscribe(() => {
      this.excludeSuccess = true;
      this.selectedDeviceIds = [];
      this.allSelected = false;
      this.selectedReason = null;
      // Update excluded list locally without API reload
      const excludedIds = new Set(items.map(i => i.deviceId));
      const newlyExcluded: IDeviceStatus[] = this.locationDevices
        .filter(d => excludedIds.has(d.id))
        .map(d => ({ id: d.id, name: d.name, exclusionReason: items.find(i => i.deviceId === d.id)!.exclusionReason } as IDeviceStatus));
      // Upsert: replace existing entries for re-excluded devices
      this.excludedDevices = [
        ...this.excludedDevices.filter(d => !excludedIds.has(d.id)),
        ...newlyExcluded
      ];
      // Mutate the shared Device objects so the device list row updates too
      this.devices.filter(d => excludedIds.has(d.id)).forEach(d => d.excluded = true);
      this.rebuildAvailable();
    }, () => {
      this.excludeError = true;
    });
  }

  removeExclusion(device: IDeviceStatus): void {
    const ref = this.dialog.open(RemoveExclusionDialogComponent, {
      data: { deviceId: device.id, deviceName: device.name },
      maxWidth: '480px'
    });
    ref.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) return;
      this.api.deleteDeviceExclusion(device.id).subscribe(() => {
        this.excludedDevices = this.excludedDevices.filter(d => d.id !== device.id);
        // Mutate the shared Device object so the device list row updates too
        const dev = this.devices.find(d => d.id === device.id);
        if (dev) dev.excluded = false;
        this.rebuildAvailable();
      });
    });
  }

  getExclusionReasonLabel(reason: DeviceExclusionReason): string {
    return getExclusionReasonKey(reason);
  }

  removeAllExclusions(): void {
    const ids = this.excludedDevices.map(d => d.id);
    if (!ids.length) return;
    const ref = this.dialog.open(RemoveExclusionDialogComponent, {
      data: { removeAll: true },
      maxWidth: '400px'
    });
    ref.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) return;
      this.api.deleteDeviceExclusions(ids).subscribe(() => {
        this.devices.filter(d => ids.includes(d.id)).forEach(d => d.excluded = false);
        this.excludedDevices = [];
        this.rebuildAvailable();
      }, () => {
        this.excludeError = true;
      });
    });
  }
}
